import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

const COUNTRIES = [
  { code: "IN", name: "India",          flag: "🇮🇳" },
  { code: "US", name: "United States",  flag: "🇺🇸" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "AU", name: "Australia",      flag: "🇦🇺" },
  { code: "CA", name: "Canada",         flag: "🇨🇦" },
  { code: "SG", name: "Singapore",      flag: "🇸🇬" },
  { code: "AE", name: "UAE",            flag: "🇦🇪" },
  { code: "NZ", name: "New Zealand",    flag: "🇳🇿" },
  { code: "ZA", name: "South Africa",   flag: "🇿🇦" },
  { code: "DE", name: "Germany",        flag: "🇩🇪" },
  { code: "FR", name: "France",         flag: "🇫🇷" },
  { code: "JP", name: "Japan",          flag: "🇯🇵" },
  { code: "NL", name: "Netherlands",    flag: "🇳🇱" },
  { code: "CH", name: "Switzerland",    flag: "🇨🇭" },
  { code: "SE", name: "Sweden",         flag: "🇸🇪" },
  { code: "NO", name: "Norway",         flag: "🇳🇴" },
  { code: "BR", name: "Brazil",         flag: "🇧🇷" },
  { code: "MY", name: "Malaysia",       flag: "🇲🇾" },
  { code: "PK", name: "Pakistan",       flag: "🇵🇰" },
  { code: "BD", name: "Bangladesh",     flag: "🇧🇩" },
  { code: "LK", name: "Sri Lanka",      flag: "🇱🇰" },
  { code: "NP", name: "Nepal",          flag: "🇳🇵" },
  { code: "SA", name: "Saudi Arabia",   flag: "🇸🇦" },
  { code: "QA", name: "Qatar",          flag: "🇶🇦" },
  { code: "KW", name: "Kuwait",         flag: "🇰🇼" },
  { code: "BH", name: "Bahrain",        flag: "🇧🇭" },
  { code: "OM", name: "Oman",           flag: "🇴🇲" },
  { code: "NG", name: "Nigeria",        flag: "🇳🇬" },
  { code: "KE", name: "Kenya",          flag: "🇰🇪" },
  { code: "EG", name: "Egypt",          flag: "🇪🇬" },
  { code: "PH", name: "Philippines",    flag: "🇵🇭" },
  { code: "ID", name: "Indonesia",      flag: "🇮🇩" },
  { code: "TH", name: "Thailand",       flag: "🇹🇭" },
  { code: "KR", name: "South Korea",    flag: "🇰🇷" },
  { code: "CN", name: "China",          flag: "🇨🇳" },
];

function Sidebar({ active }: { active: any }) {
  const { t } = useTranslation();
  const MENU = [
    { labelKey: "account.settings",      path: "/account/settings",      icon: "⚙" },
    { labelKey: "account.reports",       path: "/account/reports",       icon: "📄" },
    { labelKey: "account.accessibility", path: "/account/accessibility", icon: "♿" },
    { labelKey: "account.plans",         path: "/account/plans",         icon: "💳" },
  ];
  return (
    <div style={{ width: "220px", flexShrink: 0, background: "white", borderRadius: "14px", border: "1.5px solid #EAE6E0", padding: "16px 0", height: "fit-content" }}>
      {MENU.map(item => (
        <Link key={item.path} to={item.path}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 20px", fontSize: "14px", color: active === item.path ? "#C9A84C" : "var(--text-primary)", fontWeight: active === item.path ? 700 : 400, background: active === item.path ? "rgba(201,168,76,0.08)" : "transparent", borderLeft: active === item.path ? "3px solid #C9A84C" : "3px solid transparent", textDecoration: "none", transition: "all 0.15s" }}>
          <span>{item.icon}</span> {t(item.labelKey)}
        </Link>
      ))}
      <div style={{ borderTop: "1px solid #EAE6E0", margin: "8px 0" }}></div>
      <button onClick={() => { localStorage.removeItem("token"); window.location.href = "/"; }}
        style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "11px 20px", fontSize: "14px", color: "#C0392B", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500, borderLeft: "3px solid transparent" }}>
        <span>🚪</span> {t("account.logout")}
      </button>
    </div>
  );
}

export default function AccountSettings() {
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<{ [key: string]: string }>({
    first_name: "", last_name: "", email: "",
    country: "India", country_code: "IN",
  });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (authUser) {
      setIsGoogleUser(!!authUser.google_id);
      const parts = (authUser.name || "").split(" ");
      setUser({
        first_name:   parts[0] || "",
        last_name:    parts.slice(1).join(" ") || "",
        email:        authUser.email || "",
        country:      authUser.country      || "India",
        country_code: authUser.country_code || "IN",
      });
    }
  }, [authUser]);

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    setSaving(true);
    try {
      const res = await fetch("/api/auth/account/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          first_name:   user.first_name   || "",
          last_name:    user.last_name    || "",
          country:      user.country      || "India",
          country_code: user.country_code || "IN",
        }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (e) {
      console.error("Save failed:", e);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px",
    border: "1.5px solid #D8D4CE", borderRadius: "8px",
    fontSize: "14px", color: "var(--text-primary)",
    background: "var(--white)", outline: "none",
  };

  const selectedCountry = COUNTRIES.find(c => c.code === user.country_code);

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <a href="/account/settings">Account</a>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>Settings</span>
          </div>
          <h1>Account Settings</h1>
          <p className="ph-subtitle">Manage your profile and preferences</p>
        </div>
      </div>
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
        <Sidebar active={location.pathname} />
        <div style={{ flex: 1, background: "white", borderRadius: "14px", border: "1.5px solid #EAE6E0", padding: "28px 32px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "24px", color: "var(--text-primary)", paddingBottom: "16px", borderBottom: "1px solid #EAE6E0" }}>
            {t("account.profile")}
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
            {/* First Name */}
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#5C5C5C", marginBottom: "6px" }}>
                {t("account.firstName")}
              </label>
              <input
                value={user.first_name}
                onChange={e => setUser({ ...user, first_name: e.target.value })}
                style={inputStyle}
                onFocus={e => { e.target.style.borderColor = "#C9A84C"; }}
                onBlur={e => { e.target.style.borderColor = "#D8D4CE"; }}
              />
            </div>

            {/* Last Name */}
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#5C5C5C", marginBottom: "6px" }}>
                {t("account.lastName")}
              </label>
              <input
                value={user.last_name}
                onChange={e => setUser({ ...user, last_name: e.target.value })}
                style={inputStyle}
                onFocus={e => { e.target.style.borderColor = "#C9A84C"; }}
                onBlur={e => { e.target.style.borderColor = "#D8D4CE"; }}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#5C5C5C", marginBottom: "6px" }}>
                {t("account.email")}
              </label>
              <input
                value={user.email}
                disabled={isGoogleUser}
                style={{ ...inputStyle, opacity: isGoogleUser ? 0.6 : 1, cursor: isGoogleUser ? "not-allowed" : "text" }}
              />
            </div>

            {/* Country dropdown */}
            <div style={{ position: "relative" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#5C5C5C", marginBottom: "6px" }}>
                {t("account.country")}
              </label>
              <button
                onClick={() => setCountryOpen(!countryOpen)}
                style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #D8D4CE", borderRadius: "8px", fontSize: "14px", color: "var(--text-primary)", background: "var(--white)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left" }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "20px" }}>{selectedCountry?.flag || "🇮🇳"}</span>
                  <span>{selectedCountry?.name || "India"}</span>
                </span>
                <span style={{ fontSize: "10px" }}>▼</span>
              </button>
              {countryOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--white)", border: "1.5px solid #D8D4CE", borderRadius: "8px", maxHeight: "240px", overflowY: "auto", zIndex: 100, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                  {COUNTRIES.map(country => (
                    <button
                      key={country.code}
                      onClick={() => { setUser({ ...user, country: country.name, country_code: country.code }); setCountryOpen(false); }}
                      style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "10px 14px", background: country.code === user.country_code ? "rgba(201,168,76,0.1)" : "transparent", border: "none", cursor: "pointer", fontSize: "14px", color: "var(--text-primary)", textAlign: "left", borderBottom: "0.5px solid #EAE6E0" }}
                    >
                      <span style={{ fontSize: "20px" }}>{country.flag}</span>
                      <span style={{ flex: 1 }}>{country.name}</span>
                      {country.code === user.country_code && <span style={{ color: "#C9A84C", fontWeight: 700 }}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", padding: "10px 24px", borderRadius: "8px", fontWeight: 700, fontSize: "14px", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving..." : t("account.save")}
            </button>
            {saved && (
              <span style={{ color: "#375623", fontSize: "13px", fontWeight: 600 }}>
                {t("account.saved")}
              </span>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
